import React, { useState, useEffect, useMemo } from "react";
import useAuth from "../../../../Hooks/useAuth";
import useFirmScopeSelection, { toScopeFirmOption } from "../../../../Hooks/useFirmScopeSelection";
import { useDebounce } from "../../../../Hooks/useDebounce";
import CustomPagination from "../CustomPagination";
import BillDetailDrawer from "../BillDetailDrawer";
import {
  FaFileInvoice,
  FaChartLine,
  FaTable,
  FaSearch,
} from "react-icons/fa";
import { useLazyGetDealerRolesBillsQuery } from "../../../../store/dealerRoles/billsApi";
import { FirmDropdown } from "../../../atoms";
import FormatDateHourString from "../../../Utils/FormatDateHourString";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { EmptyState } from "../../../shared";
import { getBillTypeDetails } from "../../../../utils/billTypeDetails";

const filterTypes = [
  { key: "all", label: "Tümü" },
  { key: "Bill", label: "Fatura" },
  { key: "FreeUpload", label: "Yükleme" },
  { key: "GetReturn", label: "İade" },
  { key: "QuotaShare", label: "Kota Paylaşımı / Satışı" },
];

const DealerRolesBillsTab = ({ firm }) => {
  const { auth } = useAuth();
  const id = firm?._id;

  const [pagination, setPagination] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showChart, setShowChart] = useState(false);
  // Alt firma kapsamı — VARSAYILAN: firma + TÜM alt firmaları (depo tarafıyla
  // aynı kural, kullanıcı kararı 2026-09-14).
  const [firmOptions, setFirmOptions] = useState([]);
  const {
    requestFirmIds,
    selectedFirms,
    onChange: onScopeChange,
    hasSubFirms,
  } = useFirmScopeSelection(firmOptions);
  // Satıra tıklayınca açılan fatura detay drawer'ı
  const [detailDrawer, setDetailDrawer] = useState({ open: false, bill: null });

  // BOŞ dizi "tüm ağaç" demektir (sunucu sözleşmesi).
  const effectiveFirmIds = requestFirmIds;

  const [
    getBills,
    { data: billsResponse, isLoading: billsLoading, isFetching: billsFetching },
  ] = useLazyGetDealerRolesBillsQuery();

  // Sayfa başına GRUP sayısı (sunucu da bunu kullanır) + debounce'lu sayfa.
  const pageSize = 20;
  const debouncedPagination = useDebounce(pagination, 200);
  const debouncedSearch = useDebounce(searchTerm, 400);

  useEffect(() => {
    if (id && auth.accessToken) {
      // SAYFALAMA, ARAMA VE TÜR SÜZGECİ SUNUCUDA (sözleşme firm-bills-list
      // v1.1, #405). Tek kutu araması `search` = fatura no VEYA ürün —
      // önceden terim billNo + productName olarak gidip VE'leniyordu ve
      // arama çoğunlukla boş dönüyordu; tür süzgeci yalnız o sayfadaydı.
      getBills({
        id,
        token: auth.accessToken,
        firmIds: effectiveFirmIds,
        page: debouncedPagination,
        pageSize,
        search: debouncedSearch || "",
        ...(activeFilter !== "all" && { billType: activeFilter }),
      });
    }
  }, [
    id,
    auth.accessToken,
    getBills,
    effectiveFirmIds,
    debouncedPagination,
    debouncedSearch,
    activeFilter,
  ]);

  const billsData = useMemo(
    () => billsResponse?.data?.bills || [],
    [billsResponse]
  );
  const totalGroupCount = billsResponse?.data?.total ?? 0;

  // Seçici seçenekleri — sunucudan gelen kök + alt firma listesi; son bilinen
  // liste state'te tutulur ki yanıt arasında seçici boşalmasın.
  const serverFirms = billsResponse?.data?.firms;
  useEffect(() => {
    if (!Array.isArray(serverFirms) || serverFirms.length === 0) return;
    setFirmOptions(
      serverFirms.map(toScopeFirmOption),
    );
  }, [serverFirms]);
  // Kapsam birden fazla firmayı içeriyorsa "İşlem Firması" sütunu açılır.
  // "Tümü" hâlinde `effectiveFirmIds` BOŞ olduğu için işaretli firma sayısına
  // bakılır — aksi halde sütun tam da gerektiği anda kaybolurdu.
  const showFirmColumn = selectedFirms.length > 1;

  // Süzme sunucuda; burada yalnız sıralama.
  const filteredAndSortedBills = useMemo(
    () => [...billsData].sort((a, b) => new Date(b.createdInfo) - new Date(a.createdInfo)),
    [billsData]
  );

  // Sunucudan gelen sayfa zaten pageSize kadar grup içerir.
  const paginatedBills = filteredAndSortedBills;

  // Süzgeç değişince ilk sayfa — gecikmeli terimle, her tuşta değil.
  useEffect(() => {
    setPagination(1);
  }, [debouncedSearch, activeFilter, effectiveFirmIds]);

  const chartData = useMemo(() => {
    const billData = billsData.filter(
      (bill) => bill.billType === "Bill" && bill.price
    );
    if (billData.length === 0) return null;

    const productGroups = {};
    billData.forEach((bill) => {
      const productType = bill.productTypeName || "Bilinmeyen Ürün";
      if (!productGroups[productType]) {
        productGroups[productType] = [];
      }
      productGroups[productType].push({
        date: new Date(bill.createdInfo),
        price: bill.price,
        billNo: bill.billNo,
      });
    });

    Object.keys(productGroups).forEach((productType) => {
      productGroups[productType].sort((a, b) => a.date - b.date);
    });

    const allDates = [
      ...new Set(
        billData.map((bill) =>
          new Date(bill.createdInfo).toLocaleDateString("tr-TR")
        )
      ),
    ].sort((a, b) => new Date(a) - new Date(b));

    return allDates.map((date) => {
      const dataPoint = { date };
      Object.keys(productGroups).forEach((productType) => {
        const bill = productGroups[productType].find(
          (b) => new Date(b.date).toLocaleDateString("tr-TR") === date
        );
        dataPoint[productType] = bill ? bill.price : null;
      });
      return dataPoint;
    });
  }, [billsData]);


  const isDataLoading = billsLoading || billsFetching;

  return (
    <div className="flex flex-col h-full w-full">
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shrink-0 px-3 sm:px-6 py-3 space-y-3">
        {/* Row 1: Başlık */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <h2 className="text-base sm:text-lg font-bold text-gray-800">Faturalar</h2>
          </div>
        </div>

        {/* Row 2: Arama + Filtreler + Görünüm */}
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0 relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Fatura no veya ürün adı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 transition-all"
            />
          </div>

          {/* Alt firma süzgeci — yalnızca alt firma varsa; varsayılan tümü */}
          {hasSubFirms && (
            <div className="w-40 sm:w-52 shrink-0">
              <FirmDropdown
                compact
                multiple
                firms={firmOptions}
                value={selectedFirms}
                placeholder="Firma seçin"
                onChange={onScopeChange}
              />
            </div>
          )}

          {!showChart && (
            <div className="hidden sm:flex items-center bg-gray-100/80 p-1 rounded-xl shrink-0">
              {filterTypes.map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setActiveFilter(filter.key)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all whitespace-nowrap ${
                    activeFilter === filter.key
                      ? "bg-white text-gray-800 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center bg-gray-100/80 p-1 rounded-xl shrink-0 gap-0.5">
            <button
              onClick={() => setShowChart(false)}
              className={`p-2.5 rounded-lg transition-all ${!showChart ? "bg-white text-gray-700 shadow-sm" : "text-gray-400 hover:text-gray-600"}`}
            >
              <FaTable className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowChart(true)}
              className={`p-2.5 rounded-lg transition-all ${showChart ? "bg-white text-gray-700 shadow-sm" : "text-gray-400 hover:text-gray-600"}`}
            >
              <FaChartLine className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className={`flex-1 min-h-0 ${showChart ? 'flex flex-col' : 'overflow-y-auto'}`}>
        {isDataLoading ? (
          <>
            {/* Mobil skeleton */}
            <div className="sm:hidden divide-y divide-gray-100">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2.5 px-3 py-3 animate-pulse">
                  <div className="h-5 w-14 bg-gray-200 rounded-lg shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="h-3.5 w-3/4 bg-gray-200 rounded mb-1" />
                    <div className="h-3 w-1/2 bg-gray-100 rounded" />
                  </div>
                  <div className="shrink-0">
                    <div className="h-3.5 w-12 bg-gray-200 rounded mb-1" />
                    <div className="h-3 w-16 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop skeleton */}
            <div className="hidden sm:block">
              <table className="w-full">
                <thead className="bg-gray-50/80">
                  <tr>
                    {[
                      // İŞLEM FİRMASI en başta (depo tarafıyla aynı).
                      ...(showFirmColumn ? ["İşlem Firması"] : []),
                      "İşlem Türü",
                      "Fatura No",
                      "Ürün & Miktar",
                      "İşlemi Yapan",
                      "Tutar",
                      "Tarih",
                    ].map((h, i) => (
                      <th key={i} className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 text-left">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-3 py-2"><div className="h-4 w-20 bg-gray-200 rounded-md" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-14 bg-gray-100 rounded" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-32 bg-gray-200 rounded" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-24 bg-gray-100 rounded" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-16 bg-gray-200 rounded" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-20 bg-gray-100 rounded" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : showChart ? (
          <div className="px-4 sm:px-6 py-3 flex flex-col h-full">
            {chartData ? (
              <>
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-3 shrink-0">
                  {Object.keys(chartData[0] || {}).filter((k) => k !== "date").map((pt, i) => {
                    const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
                    return (
                      <div key={pt} className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors[i % colors.length] }} />
                        <span className="text-xs font-medium text-gray-600">{pt}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="w-full flex-1 min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 15, left: -10, bottom: 5 }}>
                      <defs>
                        {Object.keys(chartData[0] || {}).filter((k) => k !== "date").map((pt, i) => {
                          const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
                          return (
                            <linearGradient key={pt} id={`dealerRoles-gradient-${i}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={colors[i % colors.length]} stopOpacity={0.15} />
                              <stop offset="95%" stopColor={colors[i % colors.length]} stopOpacity={0} />
                            </linearGradient>
                          );
                        })}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} dy={8} interval="preserveStartEnd" />
                      <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `₺${v.toLocaleString("tr-TR")}`} width={55} />
                      <Tooltip
                        formatter={(value, name) => [`₺${value?.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`, name]}
                        labelFormatter={(label) => label}
                        contentStyle={{
                          backgroundColor: "rgba(255,255,255,0.96)",
                          backdropFilter: "blur(8px)",
                          border: "none",
                          borderRadius: "12px",
                          fontSize: "12px",
                          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                          padding: "10px 14px",
                        }}
                        labelStyle={{ fontWeight: 600, color: "#374151", marginBottom: "4px", fontSize: "11px" }}
                        cursor={{ stroke: "#cbd5e1", strokeDasharray: "4 4" }}
                      />
                      {Object.keys(chartData[0] || {}).filter((k) => k !== "date").map((pt, i) => {
                        const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
                        return (
                          <Area
                            key={pt}
                            type="monotone"
                            dataKey={pt}
                            stroke={colors[i % colors.length]}
                            strokeWidth={2.5}
                            fill={`url(#dealerRoles-gradient-${i})`}
                            dot={{ r: 3, fill: "white", stroke: colors[i % colors.length], strokeWidth: 2 }}
                            activeDot={{ r: 5, fill: colors[i % colors.length], stroke: "white", strokeWidth: 2 }}
                            connectNulls={false}
                          />
                        );
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-3">
                  <FaChartLine className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm font-semibold text-gray-600">Grafik Verisi Yok</p>
                <p className="text-xs text-gray-400 mt-0.5">Fiyat bilgisi bulunamadı.</p>
              </div>
            )}
          </div>
        ) : paginatedBills.length === 0 ? (
          <EmptyState
            message="Fatura Bulunamadı"
            suggestion="Arama kriterlerinize uygun işlem yok."
          />
        ) : (
          <>
            {/* Mobil: Satır bazlı liste */}
            <div className="sm:hidden divide-y divide-gray-100">
              {paginatedBills.map((bill) => {
                const details = getBillTypeDetails(bill.billType, bill.quotaShare);
                return (
                  <div
                    key={bill._id}
                    onClick={() => setDetailDrawer({ open: true, bill })}
                    className="flex items-center gap-2.5 px-3 py-3 cursor-pointer active:bg-gray-50"
                  >
                    <span className={`shrink-0 inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold border ${details.color}`}>
                      {details.short}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{bill.productName} <span className="text-gray-400">× <span className="font-Bold text-gray-700 tabular-nums">{bill.quota}</span></span></p>
                      <p className="text-xs text-gray-400 truncate">
                        {bill.billerName} · {bill.billNo}
                        {bill.isSubFirmBill && ` · ${bill.firmName}`}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      {bill.billType === "Bill" && bill.price ? (
                        <p className="text-sm font-Bold text-emerald-600 tabular-nums">₺{bill.price.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</p>
                      ) : (
                        <p className="text-xs text-gray-400">{details.short}</p>
                      )}
                      <p className="text-xs text-gray-400">{FormatDateHourString(bill.createdInfo)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: Kompakt tablo */}
            <div className="hidden sm:flex sm:flex-col sm:h-full overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50/80">
                  <tr>
                    {["İşlem Türü", "Fatura No", "Ürün & Miktar", "İşlemi Yapan", "Tutar", "Tarih"].map((h, i) => (
                      <th key={i} className="px-3 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200 text-left">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
              <div className="flex-1 overflow-y-auto min-h-0">
                <table className="w-full">
                  <tbody className="divide-y divide-gray-100">
                    {paginatedBills.map((bill) => {
                      const details = getBillTypeDetails(bill.billType, bill.quotaShare);
                      return (
                        <tr
                          key={bill._id}
                          onClick={() => setDetailDrawer({ open: true, bill })}
                          className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                        >
                          {/* İşlem Firması — yalnızca birden fazla firma seçiliyken */}
                          {showFirmColumn && (
                            <td className="px-3 py-3">
                              <span
                                className={`text-sm truncate inline-block max-w-[180px] align-middle ${
                                  bill.isSubFirmBill
                                    ? "text-violet-700 font-SemiBold"
                                    : "text-gray-600"
                                }`}
                                title={bill.firmName}
                              >
                                {bill.firmName || "-"}
                              </span>
                            </td>
                          )}
                          <td className="px-3 py-3">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border ${details.color}`}>
                              {details.title}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <span className="text-sm font-medium text-gray-700">{bill.billNo}</span>
                          </td>
                          <td className="px-3 py-3">
                            <span className="text-sm font-semibold text-gray-800">{bill.productName}</span>
                            <span className="text-sm text-gray-400 ml-1">× <span className="font-Bold text-gray-700 tabular-nums">{bill.quota}</span></span>
                          </td>
                          <td className="px-3 py-3">
                            <span className="text-sm text-gray-600">{bill.billerName}</span>
                          </td>
                          <td className="px-3 py-3">
                            {bill.billType === "Bill" ? (
                              <div>
                                <span className="text-sm font-Bold text-gray-800 tabular-nums">
                                  {bill.price ? `₺${bill.price.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}` : "-"}
                                </span>
                                {bill.income && (
                                  <p className="text-xs text-gray-400">Gelir: ₺{bill.income.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400 italic">{details.short}</span>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <span className="text-sm text-gray-500">{FormatDateHourString(bill.createdInfo)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Fatura detay drawer'ı — satıra tıklayınca açılır */}
      <BillDetailDrawer
        open={detailDrawer.open}
        onClose={() => setDetailDrawer({ open: false, bill: null })}
        bill={detailDrawer.bill}
        typeDetails={detailDrawer.bill ? getBillTypeDetails(detailDrawer.bill.billType, detailDrawer.bill.quotaShare) : null}
      />

      {/* Sticky Pagination */}
      {totalGroupCount > pageSize && !showChart && !isDataLoading && (
        <div className="shrink-0 border-t border-gray-200 bg-white/95 backdrop-blur-sm px-2 sm:px-4 py-1.5">
          <CustomPagination currentPage={pagination} totalCount={totalGroupCount} pageSize={pageSize} onPageChange={setPagination} />
        </div>
      )}
    </div>
  );
};

export default DealerRolesBillsTab;
